import { computeStandings, groupProgress, type Game } from "@/lib/standings";
import { formatKickoff, isFinal } from "@/lib/time";
import { Link } from "react-router-dom";
import { teamDe } from "@/lib/teamNames";

export function StandingsTable({ games, label }: { games: Game[]; label: string }) {
  const standings = computeStandings(games)[label] ?? [];
  const progress = groupProgress(games, label);
  const groupGames = games
    .filter((g) => g.phase === "group" && g.group_label === label)
    .sort((a, b) => new Date(a.kickoff_time).getTime() - new Date(b.kickoff_time).getTime());

  const status =
    progress.played === 0 ? "Noch offen" : progress.played === progress.total ? "Entschieden" : "Läuft";

  return (
    <div className="bg-card rounded-lg shadow-card overflow-hidden border border-border">
      <div className="bg-primary text-primary-foreground px-4 py-2.5 flex items-center justify-between">
        <h3 className="font-display font-extrabold text-base">Gruppe {label}</h3>
        <div className="text-[10px] font-bold uppercase tracking-wider bg-primary-foreground/15 px-2 py-1 rounded-pill">
          {progress.played}/{progress.total} · {status}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="text-left py-2 pl-3 pr-1 w-6">#</th>
              <th className="text-left py-2 px-1">Team</th>
              <th className="text-center py-2 px-1 w-7">Sp</th>
              <th className="text-center py-2 px-1 w-7">S</th>
              <th className="text-center py-2 px-1 w-7">U</th>
              <th className="text-center py-2 px-1 w-7">N</th>
              <th className="text-center py-2 px-1 w-12">Tore</th>
              <th className="text-center py-2 px-1 w-8">Diff</th>
              <th className="text-center py-2 pr-3 pl-1 w-8 font-extrabold text-foreground">Pkt</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((row, idx) => {
              const qualified = idx < 2;
              return (
                <tr
                  key={row.team}
                  className={`border-t border-border ${qualified ? "bg-emerald-50/60" : "bg-card"} ${idx >= 2 ? "text-muted-foreground" : ""}`}
                >
                  <td className={`py-2 pl-3 pr-1 font-bold tabular-nums ${qualified ? "border-l-4 border-l-emerald-500" : ""}`}>
                    {idx + 1}
                  </td>
                  <td className="py-2 px-1">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="text-base leading-none">{row.flag}</span>
                      <span className="font-semibold truncate">{teamDe(row.team)}</span>
                      {qualified && (
                        <span className="text-[9px] font-bold bg-emerald-500 text-white px-1 py-0.5 rounded leading-none">Q</span>
                      )}
                    </span>
                  </td>
                  <td className="text-center px-1 tabular-nums">{row.played}</td>
                  <td className="text-center px-1 tabular-nums">{row.wins}</td>
                  <td className="text-center px-1 tabular-nums">{row.draws}</td>
                  <td className="text-center px-1 tabular-nums">{row.losses}</td>
                  <td className="text-center px-1 tabular-nums">{row.goalsFor}:{row.goalsAgainst}</td>
                  <td className="text-center px-1 tabular-nums">
                    {row.diff > 0 ? `+${row.diff}` : row.diff}
                  </td>
                  <td className="text-center pr-3 pl-1 font-extrabold tabular-nums text-foreground">{row.points}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {groupGames.length > 0 && (
        <div className="border-t border-border bg-muted/30">
          {groupGames.map((g) => {
            const finished = g.is_finished && g.home_score != null;
            const live = !finished && Date.now() >= new Date(g.kickoff_time).getTime() && !isFinal(g.kickoff_time);
            return (
              <Link
                to={`/spiel/${g.id}`}
                key={g.id}
                className="flex items-center gap-2 px-3 py-1.5 text-[11px] border-t border-border first:border-t-0 hover:bg-card tap"
              >
                {live && <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />}
                <span className="text-muted-foreground tabular-nums w-20 shrink-0 truncate">
                  {formatKickoff(g.kickoff_time).split(",")[0]}
                </span>
                {/*
                  Each side is its own flex row so the FLAG can be `shrink-0`
                  while only the team NAME truncates. Without this, the whole
                  "{flag} {name}" lump truncates together and on narrow columns
                  it's the flag that disappears (truncation happens at the
                  logical end of the inline string, which is the away-side
                  flag).
                */}
                <span className="flex-1 min-w-0 flex items-center justify-end gap-1">
                  <span className="shrink-0">{g.home_flag}</span>
                  <span className="truncate">{teamDe(g.home_team)}</span>
                </span>
                <span className="font-bold tabular-nums w-10 shrink-0 text-center">
                  {finished ? `${g.home_score}:${g.away_score}` : live ? "LIVE" : "vs"}
                </span>
                <span className="flex-1 min-w-0 flex items-center gap-1">
                  <span className="truncate">{teamDe(g.away_team)}</span>
                  <span className="shrink-0">{g.away_flag}</span>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
